/**
 * El shell del Studio = hoy.html compuesto con @skein/ui: sidebar plegable a
 * raíl (fab y tecla «[»), migas, búsqueda, tema, usuario anclado.
 */
import { Link, Navigate, Outlet, useRouterState } from "@tanstack/react-router";
import { Fragment, useEffect, useRef, useState, type ReactNode } from "react";
import { useMsal } from "@azure/msal-react";
import { useTheme } from "@skein/design";
import { Badge, Brand, Crumbs, Envline, LabelText, MenuItem, MenuSep, Mono, NavFab, NavGroup, NavItem, NavUser, PopHead, Popover, SearchButton, Sidebar, Symbol, ThemeButton, Topbar, useToast } from "@skein/ui";
import { BrandPicker } from "./BrandPicker";
import { Palette } from "./Palette";
import { AsideSlotProvider } from "./asideSlot";
import { CrumbSlotProvider } from "./crumbSlot";
import { ApiError } from "@skein/api-client";
import { LoginPage } from "../features/login/LoginPage";
import { NAV, type CountKey } from "./nav";
import { QueuePane } from "../features/cola/QueuePane";
import { NAV_SEED, ROLE_LABEL } from "./seed";
import { initials } from "../auth/initials";
import { useBiblioteca, useEngagements, useMe, useSkills } from "../api/queries";
import { ENV } from "../env";

function useRail(): [boolean, (v: boolean | ((r: boolean) => boolean)) => void] {
  const [railed, setRailed] = useState(() => localStorage.getItem("studio-rail") === "1");
  useEffect(() => {
    document.body.classList.toggle("railed", railed);
    localStorage.setItem("studio-rail", railed ? "1" : "0");
  }, [railed]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "[" && !e.metaKey && !e.ctrlKey && !(e.target as HTMLElement).closest("input,textarea")) setRailed((r) => !r);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  return [railed, setRailed];
}

function useUserMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", esc); };
  }, [open]);
  return { open, toggle: () => setOpen((o) => !o), ref };
}

export function Shell() {
  const { instance, accounts } = useMsal();
  const me = useMe();
  // ADR-14 pt 6: con SOLO el rol de biblioteca, el Studio es la biblioteca (la
  // puerta de rol de la API niega el resto, el inspector incluido): ni se pide
  const rolStudio = me.data?.rol;
  const soloBiblioteca = rolStudio === "biblioteca";
  const skills = useSkills(!soloBiblioteca);
  const engagements = useEngagements(!soloBiblioteca);
  const biblioteca = useBiblioteca(rolStudio !== undefined && rolStudio !== "ninguno");
  const say = useToast();
  const { toggleLight } = useTheme();
  const [railed, setRailed] = useRail();
  const menu = useUserMenu();
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [palOpen, setPalOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // con Preferencias abierto, ⌘K no abre la paleta (coordinación del prototipo)
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k" && !prefsOpen) { e.preventDefault(); setPalOpen((o) => !o); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [prefsOpen]);
  const [aside, setAside] = useState<ReactNode>(null);
  const [crumbs, setCrumbs] = useState<ReactNode[] | null>(null);
  const path = useRouterState({ select: (s) => s.location.pathname });

  const name = me.data?.principal?.name || accounts[0]?.name || "—";
  const email = me.data?.principal?.email || accounts[0]?.username || "";
  // rol: cuando /me lo sirva («curadora · Skein Team»); mientras, solo la casa — nunca el email duplicado
  const rolId = me.data?.principal?.roles[0];
  const rol = rolId ? (ROLE_LABEL[rolId] ?? rolId) : undefined;
  // un socio (solo rol de biblioteca) no es del Skein Team: «socio · dueño de B · Dinero» (biblioteca.html)
  const misAreas = [...new Set((biblioteca.data?.cuadernos ?? []).filter((c) => biblioteca.data?.me.misCuadernos.includes(c.code)).map((c) => c.areaName))];
  const subtitulo = soloBiblioteca
    ? `socio${misAreas.length === 1 ? ` · dueño de ${misAreas[0]}` : misAreas.length > 1 ? ` · dueño de ${misAreas.length} áreas` : ""}`
    : rol ? `${rol} · Skein Team` : "Skein Team";
  const current = NAV.find((n) => (n.to === "/" ? path === "/" : path.startsWith(n.to)));
  // real cuando la API responde; en error sin datos, la semilla (el prototipo conserva 14/23)
  const counts: Record<CountKey, number | undefined> = {
    ...NAV_SEED, skills: skills.data?.length ?? NAV_SEED.skills, implantaciones: engagements.data?.length ?? NAV_SEED.implantaciones,
    // la biblioteca real: fichas totales; sin datos, sin número (la semilla de 86 era la demo)
    conocimiento: biblioteca.data?.cuadernos.reduce((s, c) => s + c.fichas, 0),
  };
  // hot: preguntas pendientes en MIS cuadernos (ADR-14)
  const mios = new Set(biblioteca.data?.me.misCuadernos ?? []);
  const hot: Partial<Record<CountKey, boolean>> = { conocimiento: (biblioteca.data?.cuadernos ?? []).some((c) => mios.has(c.code) && c.preguntasPendientes > 0) };
  const nav = soloBiblioteca ? NAV.filter((n) => n.to === "/conocimiento") : NAV;

  // «sin rol, nada» (ADR-14 pt 6): la vista «sin acceso» de login.html cuando la
  // API lo dice — sin rol de aplicación Y la puerta de rol encendida (un 403 en lo
  // que el Shell pide). Con la puerta apagada (STUDIO_REQUIRE_ROLE), se ve lo de siempre.
  const vetado = (e: unknown) => e instanceof ApiError && e.status === 403;
  if (rolStudio === "ninguno" && (vetado(skills.error) || vetado(engagements.error))) return <LoginPage sinRol onSalir={() => void instance.logoutRedirect()} />;
  // con solo el rol de biblioteca, todo lo demás lleva a la biblioteca
  if (soloBiblioteca && !path.startsWith("/conocimiento")) return <Navigate to="/conocimiento" />;

  let lastGroup: string | null = null;
  const showQueue = current?.queue === true;
  // /cola: la página ES la cola — nada de sidepane compartida — pero se
  // páginas con aside propio (ownAside) ocupan la misma columna del shell,
  // servido vía AsideSlotProvider (el estado se queda en la página).
  const hasOwnAside = current?.ownAside === true;
  return (
    <div className={showQueue || hasOwnAside ? "shell shell--queue" : "shell"}>
      <Sidebar railed={railed} aria-label="Studio">
        <Brand wordmark size="var(--sk-fs-l)" style={{ padding: "var(--sk-sp-2) var(--sk-sp-3) var(--sk-sp-4)", display: "flex", alignItems: "center", gap: "var(--sk-sp-2)" }}>
          <Symbol size={20} /><LabelText>Ske<i>i</i>n </LabelText><Badge tone="accent" className="sk-label-text">STUDIO</Badge><span className="brand-s"><Symbol size={16} /></span>
        </Brand>
        {nav.map((n) => {
          const group = n.group && n.group !== lastGroup ? <NavGroup key={`g-${n.group}`}>{n.group}</NavGroup> : null;
          lastGroup = n.group ?? lastGroup;
          return (
            <Fragment key={n.to}>
              {group}
              <NavItem as={Link} to={n.to} icon={n.icon} label={n.label} count={n.count ? counts[n.count] : undefined} hot={n.hot || (n.count !== undefined && hot[n.count] === true)} active={current?.to === n.to} railed={railed} />
            </Fragment>
          );
        })}
        <div className="userwrap" ref={menu.ref}>
          <NavUser initials={initials(name)} name={name} subtitle={subtitulo} open={menu.open} railed={railed} onClick={menu.toggle} />
          <Popover open={menu.open} className="userpop">
            <PopHead title={name} subtitle={rol ? `${email} · ${rol}` : email} />
            <MenuItem icon="user" onClick={() => say("Tu perfil llega con F5b.")}>Mi perfil</MenuItem>
            <MenuItem icon="gear" onClick={() => setPrefsOpen(true)}>Preferencias</MenuItem>
            <MenuSep />
            <MenuItem icon="out" danger onClick={() => void instance.logoutRedirect()}>Cerrar sesión</MenuItem>
          </Popover>
        </div>
        <Envline>{ENV.label} · eu-west</Envline>
      </Sidebar>
      <span className="fabspot"><NavFab railed={railed} onClick={() => setRailed((r) => !r)} /></span>
      <Topbar>
        <Crumbs items={
          // un detalle que sabe más pone sus migas (la ficha: cuaderno y slug)
          crumbs ? ["Studio", ...crumbs] :
          // detalle (p. ej. /skills/erosion-margen): lista enlazada + slug en mono
          current && path.length > current.to.length && current.to !== "/"
            ? ["Studio", <Link key="lista" to={current.to}>{current.label}</Link>, <Mono key="slug">{decodeURIComponent(path.slice(current.to.length + 1).split("/")[0] ?? "")}</Mono>]
            : ["Studio", current?.label ?? "—"]
        } />
        <SearchButton onClick={() => setPalOpen(true)} />
        <ThemeButton onClick={toggleLight} />
      </Topbar>
      <main className="main"><CrumbSlotProvider value={setCrumbs}><AsideSlotProvider value={hasOwnAside ? setAside : null}><Outlet /></AsideSlotProvider></CrumbSlotProvider></main>
      {showQueue && <QueuePane />}
      {hasOwnAside && aside}
      <Palette open={palOpen} onClose={() => setPalOpen(false)} onPrefs={() => setPrefsOpen(true)} onRail={() => setRailed((r) => !r)} />
      <BrandPicker open={prefsOpen} onClose={() => setPrefsOpen(false)} />
    </div>
  );
}
